import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import RelatedService from './components/RelatedService.js';
import PaymentSelect from './components/PaymentSelect.js';

const API_URL = "https://heou1vaond.execute-api.us-west-1.amazonaws.com/Prod"
const path = "/services"
const altPath = "/paymentrequest"
const freePath = "/compService"

const apiHeaders = {
    "Content-Type": 'application/json',
    "x-api-key": "nMEyHpJKWa8ks2jCvOkJ6akQcQh6Oloz6G3FW7yZ"
}

const METHOD_ID_FIELD = {
    zelle: 'numberzelle',
    venmo: 'venmoid',
    cashapp: 'cashapp_id',
    paypal: 'paypal_id',
    inperson_number: 'inperson_number',
    free: null,
};

const METHOD_LABEL = {
    zelle: 'Zelle',
    venmo: 'Venmo',
    cashapp: 'Cash App',
    paypal: 'PayPal',
    inperson_number: 'In Person',
    free: 'Free',
};

// Descriptions longer than this (or containing several line breaks) get
// clamped behind a "Read more" toggle so they never push the payment
// selector and checkout button below the fold.
const DESC_COLLAPSE_CHARS = 260;
const DESC_COLLAPSE_LINES = 4;

// A location is treated as a meeting link when it has a scheme, or reads as a
// bare domain/path ("meet.google.com/mkh-xayk-tdw"). Street addresses always
// contain spaces, so anything with whitespace is treated as an address.
const URL_WITH_SCHEME = /^[a-z][a-z0-9+.-]*:\/\//i;
const BARE_DOMAIN = /^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/i;

const isLinkLocation = (value) => {
    if (typeof value !== 'string') return false;
    const trimmed = value.trim();
    if (!trimmed || /\s/.test(trimmed)) return false;
    return URL_WITH_SCHEME.test(trimmed) || BARE_DOMAIN.test(trimmed);
};

// Local YYYY-MM-DD (avoids toISOString's UTC shift)
const toDateKey = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

// Copy of a date at local midnight
const normDay = (date) => {
    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
};

const ServicesPage = () => {
    const { id } = useParams();
    const [service, setService] = useState(null);
    const [loading, setLoading] = useState(true);
    const [relatedServices, setRelatedServices] = useState([]);
    const [quantity, setQuantity] = useState(1);
    const [buyPrice, setBuyPrice] = useState(null);
    const [emailError, setEmailError] = useState('');
    const [paymentMethod, selectMethod] = useState(null);
    const [methods, setMethods] = useState([]);
    const [email, setEmail] = useState(null);
    const navigate = useNavigate();

    // Full name states (collected on the step right before email)
    const [fullName, setFullName] = useState('');
    const [nameError, setNameError] = useState('');

    // Description expand/collapse state
    const [descExpanded, setDescExpanded] = useState(false);

    // Phone number states
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');

    // Client address states (collected only when the service is performed at the
    // client's chosen address, i.e. service.loctype === 'client').
    const [address, setAddress] = useState('');
    const [addressError, setAddressError] = useState('');

    // Scheduling states
    const [schedbool, setSchedbool] = useState(false);
    const [schedule, setSchedule] = useState([]);
    const [availability, setAvailability] = useState(null);
    const [selectedSessions, setSelectedSessions] = useState([]);
    const [currentMonth, setCurrentMonth] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(null);

    // Hotel-style day-span state (used when service.alldaybool is true).
    // A single stay: the user taps a check-in date, then a check-out date —
    // like booking nights at a hotel — instead of individual time slots.
    // Tapping the check-in cell clears the whole stay; tapping the check-out
    // cell clears just the check-out date.
    const [checkInDate, setCheckInDate] = useState(null);
    const [checkOutDate, setCheckOutDate] = useState(null);

    const [isSubmitting, setIsSubmitting] = useState(false);

    // Step-based wizard flow
    // 'details' -> 'scheduling' (if needed) -> 'name' + 'email' (if needed) -> 'phone' -> 'address' (if needed) -> submit
    const [step, setStep] = useState('details');
    const [slideDirection, setSlideDirection] = useState('next');
    const [isAnimating, setIsAnimating] = useState(false);
    const contentRef = useRef(null);

    // When service.alldaybool is true, scheduling becomes a hotel-style day-span
    // booking: pick a check-in and check-out date, no time-slot selection. The
    // number of nights drives the quantity.
    const isHotelMode = schedbool && !!service?.alldaybool;

    // When bundleqty is 1 AND scheduling is enabled (and it's NOT hotel mode), we
    // show the time-slot calendar directly on the details step and derive quantity
    // from the number of slots picked, instead of a separate scheduling step.
    const isInlineSchedule = schedbool && !isHotelMode && service?.bundleqty === 1;

    // Number of nights currently selected in hotel mode.
    const hotelNights = (isHotelMode && checkInDate && checkOutDate)
        ? Math.max(0, Math.round((checkOutDate.getTime() - checkInDate.getTime()) / 86400000))
        : 0;

    // Max overnight guests per night for this service (defaults to 1 if unset).
    const overnightCapacity = Math.max(parseInt(service?.client_count, 10) || 1, 1);

    // Location type derivations. Declared here (rather than just before render)
    // so the step-order logic can depend on isClientLoc.
    const isServerLoc = service?.loctype === 'server';
    const isClientLoc = service?.loctype === 'client';
    // A server location that's a meeting link (Zoom, Google Meet, etc.) is shown
    // as "Virtual" and isn't tappable — there's nothing to open until the
    // appointment itself.
    const isVirtualLoc = isServerLoc && isLinkLocation(service?.location);
    const locationValue = isServerLoc
        ? (isVirtualLoc ? 'Virtual' : service?.location)
        : isClientLoc
            ? 'Client Address'
            : null;

    // Description derivations. Collapse when the copy is long enough to crowd
    // out the payment section — either by raw length or by line count.
    const description = (service?.description || '').trim();
    const isLongDescription =
        description.length > DESC_COLLAPSE_CHARS ||
        description.split('\n').length > DESC_COLLAPSE_LINES;

    const verifyCountryCode = (countryCode) => {
        if (countryCode != 'US') {
            navigate('/service-unavailable');
        }
    }

    useEffect(() => {
        setLoading(true);
        setService(null);
        setQuantity(1);
        setBuyPrice(null);
        setEmailError('');
        selectMethod(null);
        setMethods([]);
        setEmail(null);
        setFullName('');
        setNameError('');
        setRelatedServices([]);
        setDescExpanded(false);
        setSchedbool(false);
        setSchedule([]);
        setAvailability(null);
        setSelectedSessions([]);
        setSelectedDate(null);
        setCheckInDate(null);
        setCheckOutDate(null);
        setCurrentMonth(new Date());
        setIsSubmitting(false);
        setPhone('');
        setPhoneError('');
        setAddress('');
        setAddressError('');
        setStep('details');

        async function fetchServiceAPI() {
            // const checkCountryCode = await axios.get(`https://ipinfo.io/json?token=1e26a7f1390529`)
            // verifyCountryCode(checkCountryCode.data.country)

            const params = { "productuid": id }

            try {
                const resp = await axios.get(`${API_URL}${path}`, {
                    params: params,
                    headers: apiHeaders
                });

                const { services, schedbool, schedule, availability } = resp.data;

                console.log(schedule)

                let updatemethods = []

                if (services.length > 0) {
                    setService(services[0]);
                    setSchedbool(schedbool || false);
                    setSchedule(schedule || []);
                    setAvailability(JSON.parse(services[0].schedule) || null);

                    if (services[0].price == 0) {
                        selectMethod('free')
                        updatemethods.push('free')
                    } else {
                        if (services[0]?.STRIPE_linked) {
                            selectMethod('card')
                            updatemethods.push('card')
                        }
                        if (services[0]?.numberzelle) {
                            updatemethods.push('zelle')
                        }
                        if (services[0]?.venmoid) {
                            updatemethods.push('venmo')
                        }
                        if (services[0]?.cashapp_id) {
                            updatemethods.push('cashapp')
                        }
                        if (services[0]?.paypal_id) {
                            updatemethods.push('paypal')
                        }
                        if (services[0]?.inperson_number) {
                            updatemethods.push('inperson_number')
                        }
                    }

                    setMethods(updatemethods)

                    if (services.length > 1) {
                        setRelatedServices(services.slice(1))
                    }

                    if (services[0].discountedbool) {
                        setBuyPrice(services[0].discountedprice)
                    } else {
                        setBuyPrice(services[0].price)
                    }
                }
                setLoading(false);
            } catch (e) {
                console.log("Error fetching service", e);
                setLoading(false);
            }
        }

        fetchServiceAPI();
    }, [id]);

    // Pre-cache the hero image so it never re-fetches when toggling steps
    useEffect(() => {
        if (service?.media?.[0]) {
            const img = new Image();
            img.src = service.media[0];
        }
    }, [service]);

    // Hotel mode: whenever the check-in/check-out range changes, rebuild the
    // booked "sessions" (one all-day entry per night) and set quantity to the
    // number of nights so pricing/checkout flow through unchanged.
    useEffect(() => {
        if (!isHotelMode || !service) return;
        if (checkInDate && checkOutDate && checkOutDate.getTime() > checkInDate.getTime()) {
            const sessions = [];
            const cur = new Date(checkInDate);
            cur.setHours(0, 0, 0, 0);
            const end = new Date(checkOutDate);
            end.setHours(0, 0, 0, 0);
            while (cur < end) {
                sessions.push({
                    // Local date key so stored nights match the capacity lookup
                    date: toDateKey(cur),
                    time: 0,
                    end_time: 1440,
                    display: 'Overnight',
                    duration: 1440,
                    productuid: service.productuid
                });
                cur.setDate(cur.getDate() + 1);
            }
            setSelectedSessions(sessions);
            setQuantity(Math.max(sessions.length, 1));
        } else {
            setSelectedSessions([]);
            setQuantity(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkInDate, checkOutDate, isHotelMode]);

    // ── Navigation helpers ──
    const needsEmailStep = paymentMethod && paymentMethod !== 'card';

    const getStepOrder = useCallback(() => {
        const steps = ['details'];
        // Scheduling comes right after details — before the email/phone inputs —
        // when scheduling is enabled and isn't handled inline. Hotel mode is
        // handled inline on the details step, so it never gets its own step.
        if (schedbool && !isInlineSchedule && !isHotelMode) steps.push('scheduling');
        // The full-name step always comes immediately before the email step.
        if (needsEmailStep) {
            steps.push('name');
            steps.push('email');
        }
        steps.push('phone');
        // For client-location services the work happens at an address the client
        // supplies, so collect it as the final step before checkout.
        if (isClientLoc) steps.push('address');
        return steps;
    }, [needsEmailStep, schedbool, isInlineSchedule, isHotelMode, isClientLoc]);

    const slideToNext = (nextStep) => {
        if (isAnimating) return;
        setIsAnimating(true);
        setSlideDirection('next');
        setTimeout(() => {
            setStep(nextStep);
            window.scrollTo(0, 0);
            setTimeout(() => setIsAnimating(false), 350);
        }, 200);
    };

    const slideToPrev = (prevStep) => {
        if (isAnimating) return;
        setIsAnimating(true);
        setSlideDirection('prev');
        setTimeout(() => {
            setStep(prevStep);
            window.scrollTo(0, 0);
            setTimeout(() => setIsAnimating(false), 350);
        }, 200);
    };

    const handleBack = () => {
        const steps = getStepOrder();
        const currentIndex = steps.indexOf(step);
        if (currentIndex > 0) {
            slideToPrev(steps[currentIndex - 1]);
        }
    };

    // Advance from a given step to the next one in the current order.
    // If it's the final step in the flow, trigger checkout instead.
    const advanceFrom = (currentStep) => {
        const steps = getStepOrder();
        const nextIdx = steps.indexOf(currentStep) + 1;
        if (nextIdx < steps.length) {
            slideToNext(steps[nextIdx]);
        } else {
            triggerCheckout();
        }
    };

    const handleProceedFromDetails = () => advanceFrom('details');

    const handleScheduleConfirm = () => advanceFrom('scheduling');

    const handleProceedFromName = () => {
        if (!isNameValid()) {
            setNameError('Please enter your full name');
            return;
        }
        advanceFrom('name');
    };

    const handleProceedFromEmail = () => advanceFrom('email');

    const handleProceedFromPhone = () => {
        if (!isPhoneValid()) return;
        advanceFrom('phone');
    };

    const handleProceedFromAddress = () => {
        if (!isAddressValid()) {
            setAddressError('Please enter a valid address');
            return;
        }
        advanceFrom('address');
    };

    const triggerCheckout = () => {
        if (paymentMethod === 'card') {
            handleSubmit();
        } else {
            handleSubmitAlternate(paymentMethod);
        }
    };

    // Whether the user can advance past the details step
    const canProceedFromDetails = () => {
        if (!paymentMethod) return false;
        if (isHotelMode && hotelNights < 1) return false;
        if (isInlineSchedule && selectedSessions.length === 0) return false;
        return true;
    };

    // ── Scheduling helpers (unchanged logic) ──
    const minutesToTime = (minutes) => {
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        const period = hours >= 12 ? 'PM' : 'AM';
        const displayHours = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        return `${displayHours}:${mins.toString().padStart(2, '0')} ${period}`;
    };

    const getDayName = (date) => {
        const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        return days[date.getDay()];
    };

    const getAvailableTimeSlots = (date) => {
        if (!availability || !date) return [];
        const dayName = getDayName(date);
        const dayAvailability = availability[dayName];
        if (!dayAvailability || !dayAvailability.enabled) return [];

        const slots = [];
        const dateStr = date.toISOString().split('T')[0];
        const bookedSlots = schedule.filter(s => s.date === dateStr);
        const sessionDuration = service?.schedduration || 30;
        const groupType = service?.grouptype;

        if (groupType === 'group') {
            const maxClients = parseInt(service.client_count);
            for (const slot of dayAvailability.selectedSlots) {
                let overlapCount = 0;
                for (const booked of bookedSlots) {
                    const sessionStart = parseInt(booked.start_time);
                    const sessionEnd = parseInt(booked.end_time);
                    if (slot.start < sessionEnd && slot.end > sessionStart) {
                        overlapCount++;
                    }
                }
                if (overlapCount < maxClients) {
                    slots.push({
                        time: slot.start, end_time: slot.end,
                        duration: slot.end - slot.start,
                        display: minutesToTime(slot.start), date: dateStr
                    });
                }
            }
        } else {
            const wouldOverlap = (slotStartTime, slotDuration) => {
                const slotEndTime = slotStartTime + slotDuration;
                for (const booked of bookedSlots) {
                    const sessionStart = parseInt(booked.start_time);
                    const sessionEnd = parseInt(booked.end_time);
                    if (slotStartTime < sessionEnd && slotEndTime > sessionStart) return true;
                }
                return false;
            };
            for (let time = dayAvailability.startTime; time + sessionDuration <= dayAvailability.endTime; time += 30) {
                if (!wouldOverlap(time, sessionDuration)) {
                    slots.push({ time, display: minutesToTime(time), date: dateStr });
                }
            }
        }
        return slots;
    };

    const getDaysInMonth = (date) => {
        const year = date.getFullYear();
        const month = date.getMonth();
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);
        const daysInMonth = lastDay.getDate();
        const startingDayOfWeek = firstDay.getDay();
        const days = [];
        for (let i = 0; i < startingDayOfWeek; i++) days.push(null);
        for (let i = 1; i <= daysInMonth; i++) days.push(new Date(year, month, i));
        return days;
    };

    // Hotel mode: a blocker (kind === 'blocker' from the API) takes the night off
    const isDateBlocked = (date) => {
        const key = toDateKey(date);
        return schedule.some(s => s.date === key && s.kind === 'blocker');
    };

    // Hotel mode: number of existing overnight bookings on this night
    const overnightBookedCount = (date) => {
        const key = toDateKey(date);
        return schedule.filter(s => s.date === key && s.kind === 'booking').length;
    };

    // Hotel mode: night is at capacity (client_count)
    const isNightFull = (date) => overnightBookedCount(date) >= overnightCapacity;

    const isDateAvailable = (date) => {
        if (!date) return false;
        const target = normDay(date);
        const today = normDay(new Date());
        if (target < today) return false;

        // Overnight stays ignore weekly availability; a night is off only when a
        // blocker covers it or it's already booked to capacity.
        if (isHotelMode) return !isDateBlocked(date) && !isNightFull(date);

        if (!availability) return false;
        const dayName = getDayName(date);
        const dayAvailability = availability[dayName];
        if (!dayAvailability || !dayAvailability.enabled) return false;

        if (service?.grouptype === 'group') {
            return dayAvailability.selectedSlots && dayAvailability.selectedSlots.length > 0;
        }
        return true;
    };

    const handleDateSelect = (date) => {
        if (isDateAvailable(date)) setSelectedDate(date);
    };

    const handleTimeSlotSelect = (slot) => {
        const sessionData = {
            date: slot.date, time: slot.time, end_time: slot.end_time,
            display: slot.display, duration: slot.duration || service.schedduration,
            productuid: service.productuid
        };
        const existingIndex = selectedSessions.findIndex(
            s => s.date === sessionData.date && s.time === sessionData.time
        );
        if (existingIndex > -1) {
            const updated = selectedSessions.filter((_, i) => i !== existingIndex);
            setSelectedSessions(updated);
            // In inline mode, quantity tracks the number of selected slots.
            if (isInlineSchedule) {
                setQuantity(Math.max(updated.length, 1));
            }
        } else {
            if (isInlineSchedule) {
                // Inline mode: no cap — each new slot bumps the quantity.
                const updated = [...selectedSessions, sessionData];
                setSelectedSessions(updated);
                setQuantity(updated.length);
            } else if (selectedSessions.length < quantity * (service?.bundleqty || 1)) {
                setSelectedSessions(prev => [...prev, sessionData]);
            }
        }
    };

    const handleRemoveSession = (index) => {
        const updated = selectedSessions.filter((_, i) => i !== index);
        setSelectedSessions(updated);
        if (isInlineSchedule) {
            setQuantity(Math.max(updated.length, 1));
        }
    };

    const isSlotSelected = (slot) => {
        return selectedSessions.some(s => s.date === slot.date && s.time === slot.time);
    };

    // ── Hotel-style day-span helpers (used when isHotelMode) ──
    const isCheckIn = (date) => !!checkInDate && normDay(date).getTime() === checkInDate.getTime();
    const isCheckOut = (date) => !!checkOutDate && normDay(date).getTime() === checkOutDate.getTime();
    const isInStay = (date) => {
        if (!checkInDate || !checkOutDate) return false;
        const t = normDay(date).getTime();
        return t > checkInDate.getTime() && t < checkOutDate.getTime();
    };

    // Every night between start (inclusive) and end (exclusive) must be available.
    const rangeIsAvailable = (start, end) => {
        const cur = normDay(start);
        const e = normDay(end);
        while (cur < e) {
            if (!isDateAvailable(cur)) return false;
            cur.setDate(cur.getDate() + 1);
        }
        return true;
    };

    // When a check-in is chosen but no check-out yet, a stay can't extend past
    // the first "marked off" (unavailable) day that follows check-in — booking
    // beyond it would require an unavailable night. This returns that first
    // unavailable day after check-in, which is the latest date the calendar
    // allows as a check-out; everything strictly after it is disabled.
    //
    // Example: check-in Monday with Saturday blocked/full → this returns Saturday,
    // so Tue–Sat stay clickable as check-out while later days are not.
    const getMaxCheckoutDate = () => {
        if (!checkInDate) return null;
        const cur = normDay(checkInDate);
        cur.setDate(cur.getDate() + 1); // earliest possible check-out (1 night)
        // Walk forward across consecutive available days; stop at the first
        // unavailable one. Cap the search so a fully-available schedule (e.g. 24/7
        // boarding, where every day is bookable) doesn't loop forever — in that
        // case there's no cutoff and any future check-out is allowed.
        let guard = 0;
        while (isDateAvailable(cur)) {
            cur.setDate(cur.getDate() + 1);
            if (++guard > 366) return null; // no unavailable day within a year → no cutoff
        }
        return cur;
    };

    const handleHotelDateSelect = (date) => {
        const d = normDay(date);

        // Tapping the current check-in cell deselects the whole stay.
        if (checkInDate && d.getTime() === checkInDate.getTime()) {
            setCheckInDate(null);
            setCheckOutDate(null);
            return;
        }
        // Tapping the current check-out cell deselects just the check-out date.
        if (checkOutDate && d.getTime() === checkOutDate.getTime()) {
            setCheckOutDate(null);
            return;
        }

        // Choosing a check-out: the departure day isn't a booked night, so it only
        // needs every night before it to be available (it may itself be full or
        // blocked). Ignore taps past the first marked-off day after check-in.
        if (checkInDate && !checkOutDate && d.getTime() > checkInDate.getTime()) {
            const maxCheckout = getMaxCheckoutDate();
            if (maxCheckout && d.getTime() > maxCheckout.getTime()) return;
            if (rangeIsAvailable(checkInDate, d)) {
                setCheckOutDate(d);
                return;
            }
        }

        if (!isDateAvailable(date)) return;

        // No check-in yet, or a full stay already set → start a fresh stay.
        if (!checkInDate || checkOutDate) {
            setCheckInDate(d);
            setCheckOutDate(null);
            return;
        }

        // Check-in is set, no check-out yet.
        if (d.getTime() < checkInDate.getTime()) {
            // An earlier date becomes the new check-in.
            setCheckInDate(d);
        } else if (rangeIsAvailable(checkInDate, d)) {
            setCheckOutDate(d);
        } else {
            // Span crosses an unavailable day → restart from the new date.
            setCheckInDate(d);
        }
    };

    const fmtHotelDate = (d) =>
        d ? d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }) : '—';

    const formatPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return '';
        const cleaned = phoneNumber.replace(/\D/g, '');
        if (cleaned.length === 11 && cleaned[0] === '1') {
            return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
        } else if (cleaned.length === 10) {
            return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
        }
        return phoneNumber;
    };

    const formatPhoneDisplay = (raw) => {
        if (!raw) return '';
        if (raw.length <= 3) return `(${raw}`;
        if (raw.length <= 6) return `(${raw.slice(0, 3)}) ${raw.slice(3)}`;
        return `(${raw.slice(0, 3)}) ${raw.slice(3, 6)}-${raw.slice(6)}`;
    };

    const handlePhoneChange = (e) => {
        const input = e.target.value;
        const digits = input.replace(/\D/g, '').slice(0, 10);
        setPhone(digits);
        if (digits.length === 0) setPhoneError('');
        else if (digits.length < 10) setPhoneError('Please enter a valid 10-digit phone number');
        else setPhoneError('');
    };

    const isPhoneValid = () => phone.length === 10 && phoneError === '';

    // A full name just needs to be non-trivial.
    const isNameValid = () => fullName.trim().length >= 2;

    // A client-supplied address just needs to be non-trivial.
    const isAddressValid = () => address.trim().length >= 5;

    const handleQuantityIncrease = () => {
        setQuantity(prev => Math.min(prev + 1, 99));
        setSelectedSessions([]);
    };

    const handleQuantityDecrease = () => {
        setQuantity(prev => Math.max(prev - 1, 1));
        setSelectedSessions([]);
    };

    const handleSubmitAlternate = async (method) => {
        setIsSubmitting(true);
        const idField = METHOD_ID_FIELD[method];
        const methodID = idField ? (service[idField] ?? '') : '';

        if (method !== 'free' && !methodID) {
            alert('This payment method is not set up for this service.');
            setIsSubmitting(false);
            return;
        }

        const submitPath = method === 'free' ? freePath : altPath;
        const navparams = {
            method: METHOD_LABEL[method] || method,
            methodID,
            amount: buyPrice * quantity,
        };
        const APIParams = {
            fullName: fullName.trim(),
            email, phone, method, methodID,
            address: isClientLoc ? address.trim() : null,
            purchaseCart: [{
                productuid: service.productuid, serveruid: service.serveruid,
                name: service.name, qty: quantity, price: buyPrice,
                discountedbool: service.discountedbool,
                discountedprice: service.discountedprice, bundleqty: service.bundleqty
            }],
            scheduledSessions: schedbool ? selectedSessions : [],
            serviceAddress: isClientLoc ? address.trim() : service.location
        };
        try {
            const resp = await axios.post(`${API_URL}${submitPath}`, APIParams, { headers: apiHeaders });
            if (resp.data.statusCode === 200 && resp.data.success) {
                setTimeout(() => setIsSubmitting(false), 2000);
                navigate('/purchase-confirm-mobile', { state: navparams });
            } else {
                alert(resp.data.body);
                setIsSubmitting(false);
            }
        } catch (e) {
            console.log(JSON.stringify(e));
            alert(JSON.stringify(e));
            setIsSubmitting(false);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        const params = {
            phone,
            address: isClientLoc ? address.trim() : undefined,
            purchaseCart: [{
                productuid: service.productuid, serveruid: service.serveruid,
                name: service.name, qty: quantity, price: buyPrice,
                media: service.media, discountedbool: service.discountedbool,
                discountedprice: service.discountedprice, bundleqty: service.bundleqty
            }],
            scheduledSessions: schedbool ? selectedSessions : []
        };
        try {
            const resp = await axios.post(`${API_URL}${path}`, params, { headers: apiHeaders });
            if (resp.data.statusCode === 200 && resp.data.success) {
                const checkoutUrl = JSON.parse(resp.data.body).url;
                window.location.href = checkoutUrl;
                setTimeout(() => setIsSubmitting(false), 2000);
            } else {
                alert(resp.data.body);
                setIsSubmitting(false);
            }
        } catch (e) {
            alert("Purchase could not be completed");
            setIsSubmitting(false);
        }
    };

    const totalSessions = quantity * (service?.bundleqty || 1);

    // ── Step index ──
    const stepOrder = getStepOrder();
    const isLastStep = (s) => stepOrder[stepOrder.length - 1] === s;

    // ── Render helpers ──

    // Service description, shown just above the payment selector on the details
    // step. Long copy is clamped to a few lines with a fade-out and a
    // "Read more" toggle so the checkout controls stay reachable.
    const renderDescription = () => {
        if (!description) return null;
        const collapsed = isLongDescription && !descExpanded;
        return (
            <div className="description-section">
                <span className="section-label">About this service</span>
                <div className={`description-body ${collapsed ? 'collapsed' : ''}`}>
                    <p className="description-text">{description}</p>
                    {collapsed && <div className="description-fade" />}
                </div>
                {isLongDescription && (
                    <button
                        type="button"
                        className="description-toggle"
                        onClick={() => setDescExpanded(prev => !prev)}
                        aria-expanded={descExpanded}
                    >
                        <span>{descExpanded ? 'Show less' : 'Read more'}</span>
                        <i className={`fas fa-chevron-${descExpanded ? 'up' : 'down'}`}></i>
                    </button>
                )}
            </div>
        );
    };

    // Shared calendar+slots UI used by both the inline details step and the
    // dedicated scheduling step. `capSessions` caps the number of slots a user
    // can pick; pass `null` for unlimited (inline mode).
    const renderCalendarUI = ({ capSessions }) => (
        <>
            <div className="schedule-grid">
                {/* Calendar */}
                <div className="cal-card">
                    <div className="cal-header">
                        <button className="cal-nav" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <h3 className="cal-month">{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                        <button className="cal-nav" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    <div className="cal-weekdays">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                            <div key={i} className="cal-weekday">{d}</div>
                        ))}
                    </div>
                    <div className="cal-days">
                        {getDaysInMonth(currentMonth).map((date, index) => {
                            if (!date) return <div key={`e-${index}`} className="cal-day empty"></div>;
                            const avail = isDateAvailable(date);
                            const sel = selectedDate && date.toDateString() === selectedDate.toDateString();
                            const hasSess = selectedSessions.some(s => s.date === date.toISOString().split('T')[0]);
                            return (
                                <div
                                    key={index}
                                    className={`cal-day ${!avail ? 'off' : ''} ${sel ? 'selected' : ''} ${hasSess ? 'booked' : ''}`}
                                    onClick={() => handleDateSelect(date)}
                                >
                                    <span>{date.getDate()}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Time slots */}
                <div className="slots-card">
                    {selectedDate ? (
                        <>
                            <h4 className="slots-heading">
                                {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                            </h4>
                            <div className="slots-grid">
                                {getAvailableTimeSlots(selectedDate).length > 0 ? (
                                    getAvailableTimeSlots(selectedDate).map((slot, i) => (
                                        <button
                                            key={i}
                                            className={`slot-btn ${isSlotSelected(slot) ? 'active' : ''}`}
                                            onClick={() => handleTimeSlotSelect(slot)}
                                            disabled={!isSlotSelected(slot) && capSessions !== null && selectedSessions.length >= capSessions}
                                        >
                                            {slot.display}
                                        </button>
                                    ))
                                ) : (
                                    <p className="slots-empty">No available slots for this date</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="slots-placeholder">
                            <i className="fas fa-calendar-day"></i>
                            <p>Select a date to view times</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Selected sessions summary */}
            {selectedSessions.length > 0 && (
                <div className="selected-summary">
                    <h4 className="summary-title">Selected Sessions</h4>
                    <div className="summary-list">
                        {selectedSessions.map((sess, i) => (
                            <div key={i} className="summary-item">
                                <span>{new Date(sess.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {sess.display}</span>
                                <button className="summary-remove" onClick={() => handleRemoveSession(i)}>
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </>
    );

    // Hotel-style day-span calendar (no time slots). Used on the details step
    // when isHotelMode. The user taps a check-in date then a check-out date.
    // Tapping the check-in cell clears the stay; tapping check-out clears it.
    //
    // Once a check-in is picked (and no check-out yet), every date strictly
    // after the first marked-off day following check-in is disabled, because a
    // stay can't span an unavailable night. That marked-off day itself stays
    // selectable as the check-out, since the guest doesn't stay that night.
    const renderHotelCalendar = () => {
        // Only constrain the calendar while the user is actively picking a
        // check-out (check-in set, check-out not yet chosen).
        const selectingCheckout = !!checkInDate && !checkOutDate;
        const maxCheckout = selectingCheckout ? getMaxCheckoutDate() : null;

        return (
            <div className="hotel-schedule">
                <div className="cal-card hotel-cal">
                    <div className="cal-header">
                        <button className="cal-nav" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                            <i className="fas fa-chevron-left"></i>
                        </button>
                        <h3 className="cal-month">{currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h3>
                        <button className="cal-nav" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                            <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                    <div className="cal-weekdays">
                        {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                            <div key={i} className="cal-weekday">{d}</div>
                        ))}
                    </div>
                    <div className="cal-days">
                        {getDaysInMonth(currentMonth).map((date, index) => {
                            if (!date) return <div key={`e-${index}`} className="cal-day empty"></div>;
                            const avail = isDateAvailable(date);
                            // Disable any date past the first marked-off day after
                            // check-in while the user is selecting a check-out.
                            const pastCutoff = maxCheckout && normDay(date).getTime() > maxCheckout.getTime();
                            // The first marked-off day itself is a valid check-out.
                            const isCheckoutCandidate = maxCheckout && normDay(date).getTime() === maxCheckout.getTime();
                            const ci = isCheckIn(date);
                            const co = isCheckOut(date);
                            const inStay = isInStay(date);
                            const disabled = (!avail && !isCheckoutCandidate && !co) || pastCutoff;
                            return (
                                <div
                                    key={index}
                                    className={`cal-day ${disabled ? 'off' : ''} ${ci ? 'checkin' : ''} ${co ? 'checkout' : ''} ${inStay ? 'in-range' : ''}`}
                                    onClick={() => { if (!pastCutoff) handleHotelDateSelect(date); }}
                                >
                                    <span>{date.getDate()}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>

                <div className="hotel-summary">
                    <div className="hotel-dates">
                        <div className="hotel-date-block">
                            <span className="hotel-date-label">Check-in</span>
                            <span className={`hotel-date-value ${!checkInDate ? 'empty' : ''}`}>{fmtHotelDate(checkInDate)}</span>
                        </div>
                        <i className="fas fa-arrow-right hotel-arrow"></i>
                        <div className="hotel-date-block hotel-date-block-right">
                            <span className="hotel-date-label">Check-out</span>
                            <span className={`hotel-date-value ${!checkOutDate ? 'empty' : ''}`}>{fmtHotelDate(checkOutDate)}</span>
                        </div>
                    </div>
                    {hotelNights > 0 ? (
                        <div className="hotel-total">
                            <div className="hotel-total-left">
                                <i className="fas fa-moon"></i>
                                <span>{hotelNights} night{hotelNights !== 1 ? 's' : ''}</span>
                            </div>
                            <span className="hotel-total-price">${(buyPrice * hotelNights).toFixed(2)}</span>
                        </div>
                    ) : checkInDate ? (
                        <p className="hotel-hint">Now select your check-out date (tap check-in again to clear)</p>
                    ) : (
                        <p className="hotel-hint">Select your check-in date to begin</p>
                    )}
                </div>
            </div>
        );
    };

    const renderDetailsStep = () => (
        <div className="step-content">
            {/* Hero image */}
            {service?.media?.length > 0 && (
                <div className="hero-image-wrapper">
                    <img
                        src={service.media[0]}
                        alt={service?.name || "Service"}
                        className="hero-image"
                        loading="eager"
                        decoding="sync"
                    />
                    <div className="hero-image-overlay" />
                </div>
            )}

            <div className="step-body">
                <span className="server-label">{service?.serverName}</span>
                <h1 className="step-title">{service?.name}</h1>

                <div className="meta-pills">
                    <span className="meta-pill">
                        {isHotelMode
                            ? `${hotelNights} night${hotelNights !== 1 ? 's' : ''} selected`
                            : isInlineSchedule
                                ? `${selectedSessions.length} session${selectedSessions.length !== 1 ? 's' : ''} selected`
                                : `${service?.bundleqty * quantity} sessions`}
                    </span>
                    <span className="meta-dot">·</span>
                    <span className="meta-pill">{service?.category}</span>
                </div>

                {/* Scheduling area:
                    - Hotel mode (alldaybool): pick a check-in/check-out day span
                    - Inline mode (bundleqty 1): pick time slots
                    - Otherwise: a plain quantity stepper */}
                {isHotelMode ? (
                    <div className="inline-schedule">
                        <span className="section-label">Select your dates</span>
                        {renderHotelCalendar()}
                    </div>
                ) : isInlineSchedule ? (
                    <div className="inline-schedule">
                        <span className="section-label">Pick your session times</span>
                        {renderCalendarUI({ capSessions: null })}
                    </div>
                ) : (
                    /* Quantity (standard flow) */
                    <div className="quantity-section">
                        <span className="section-label">Quantity</span>
                        <div className="quantity-row">
                            <button className="qty-btn" onClick={handleQuantityDecrease} disabled={quantity <= 1}>
                                <i className="fas fa-minus"></i>
                            </button>
                            <div className="qty-display">
                                <span className="qty-number">{quantity}</span>
                                <span className="qty-label">package{quantity > 1 ? 's' : ''}</span>
                            </div>
                            <button className="qty-btn" onClick={handleQuantityIncrease} disabled={quantity >= 99}>
                                <i className="fas fa-plus"></i>
                            </button>
                        </div>
                    </div>
                )}

                {/* Service description — collapsed when long */}
                {renderDescription()}

                {/* Payment method */}
                <div className="payment-section">
                    <span className="section-label">Payment</span>
                    <PaymentSelect
                        selectMethod={selectMethod}
                        selectedMethod={paymentMethod}
                        methods={methods}
                    />
                </div>

                <button
                    className={`wizard-btn primary ${!canProceedFromDetails() ? 'disabled' : ''}`}
                    onClick={handleProceedFromDetails}
                    disabled={!canProceedFromDetails()}
                >
                    {isHotelMode && hotelNights < 1 ? (
                        <span>Select your dates to continue</span>
                    ) : isInlineSchedule && selectedSessions.length === 0 ? (
                        <span>Select a time slot to continue</span>
                    ) : (
                        <>
                            <span>Continue — ${(buyPrice * quantity).toFixed(2)}</span>
                            <i className="fas fa-arrow-right"></i>
                        </>
                    )}
                </button>

                {/* Business details */}
                {(locationValue || service?.serverNumber) && (
                    <div className="info-cards">
                        {locationValue && (
                            <div
                                className={`info-card ${(isClientLoc || isVirtualLoc) ? 'static' : ''}`}
                                onClick={(isServerLoc && !isVirtualLoc) ? () => {
                                    const location = encodeURIComponent(service.location);
                                    const userAgent = navigator.userAgent;
                                    if (/iPad|iPhone|iPod/.test(userAgent)) {
                                        window.open(`maps://maps.google.com/maps?q=${location}`);
                                    } else if (/Android/.test(userAgent)) {
                                        window.open(`intent://maps.google.com/maps?q=${location}#Intent;scheme=https;package=com.google.android.apps.maps;end`);
                                    } else {
                                        window.open(`https://maps.google.com/maps?q=${location}`, '_blank');
                                    }
                                } : undefined}
                            >
                                <div className="info-card-icon">
                                    <i className="fas fa-map-marker-alt"></i>
                                </div>
                                <div className="info-card-text">
                                    <span className="info-card-label">Location</span>
                                    <span className="info-card-value">{locationValue}</span>
                                </div>
                                {isServerLoc && !isVirtualLoc && <i className="fas fa-chevron-right info-card-arrow"></i>}
                            </div>
                        )}
                        {service?.serverNumber && (
                            <a href={`tel:${service.serverNumber}`} className="info-card" style={{ textDecoration: 'none' }}>
                                <div className="info-card-icon phone-icon">
                                    <i className="fas fa-phone"></i>
                                </div>
                                <div className="info-card-text">
                                    <span className="info-card-label">Contact</span>
                                    <span className="info-card-value">{formatPhoneNumber(service.serverNumber)}</span>
                                </div>
                                <i className="fas fa-chevron-right info-card-arrow"></i>
                            </a>
                        )}
                    </div>
                )}
            </div>

            {/* Related services */}
            {relatedServices?.length > 0 && (
                <div className="related-section">
                    <h2 className="related-title">Related Packages</h2>
                    <p className="related-subtitle">More packages you might like</p>
                    <div className="related-scroll">
                        {relatedServices.map((s, index) => (
                            <RelatedService key={index} service={s} />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );

    // Collected right before the email step. autoComplete="name" lets the
    // browser / password manager offer the user's saved full name.
    const renderNameStep = () => (
        <div className="step-content">
            <div className="step-body step-body-centered">
                <div className="step-icon-wrapper">
                    <i className="fas fa-user"></i>
                </div>
                <h1 className="step-title">Your full name</h1>
                <p className="step-subtitle">So the business knows who they're working with.</p>

                <div className="input-group">
                    <label className="input-label" htmlFor="fullName">Full name</label>
                    <input
                        type="text"
                        id="fullName"
                        name="name"
                        autoComplete="name"
                        autoCapitalize="words"
                        spellCheck={false}
                        className={`wizard-input ${nameError ? 'input-error' : ''}`}
                        placeholder="Jane Doe"
                        value={fullName}
                        onChange={(e) => {
                            setFullName(e.target.value);
                            if (nameError && e.target.value.trim().length >= 2) setNameError('');
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && isNameValid()) handleProceedFromName();
                        }}
                    />
                    {nameError && <span className="input-error-text">{nameError}</span>}
                </div>

                <button
                    className={`wizard-btn primary ${!isNameValid() ? 'disabled' : ''}`}
                    onClick={handleProceedFromName}
                    disabled={!isNameValid()}
                >
                    <span>Next</span>
                    <i className="fas fa-arrow-right"></i>
                </button>
            </div>
        </div>
    );

    const renderEmailStep = () => (
        <div className="step-content">
            <div className="step-body step-body-centered">
                <div className="step-icon-wrapper">
                    <i className="fas fa-envelope"></i>
                </div>
                <h1 className="step-title">Your email address</h1>
                <p className="step-subtitle">We'll send your receipt and session details here.</p>

                <div className="input-group">
                    <label className="input-label">Email</label>
                    <input
                        type="email"
                        name="email"
                        autoComplete="email"
                        className={`wizard-input ${emailError ? 'input-error' : ''}`}
                        placeholder="you@example.com"
                        value={email || ''}
                        onChange={(e) => {
                            setEmail(e.target.value);
                            if (e.target.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.target.value)) {
                                setEmailError('Please enter a valid email');
                            } else {
                                setEmailError('');
                            }
                        }}
                        autoFocus
                    />
                    {emailError && <span className="input-error-text">{emailError}</span>}
                </div>

                <button
                    className={`wizard-btn primary ${(!email || emailError) ? 'disabled' : ''}`}
                    onClick={handleProceedFromEmail}
                    disabled={!email || !!emailError}
                >
                    <span>Next</span>
                    <i className="fas fa-arrow-right"></i>
                </button>
            </div>
        </div>
    );

    const renderPhoneStep = () => {
        // Phone is the final step unless a client-address step follows it.
        const phoneIsLast = isLastStep('phone');
        return (
            <div className="step-content">
                <div className="step-body step-body-centered">
                    <div className="step-icon-wrapper purple">
                        <i className="fas fa-phone"></i>
                    </div>
                    <h1 className="step-title">Your phone number</h1>
                    <p className="step-subtitle">
                        This number will be added to your client profile so the business can contact you if needed.
                        </p>
                    <div className="input-group">
                        <label className="input-label">Phone number</label>
                        <div className="phone-field">
                            <span className="phone-prefix">+1</span>
                            <input
                                type="tel"
                                name="phone"
                                autoComplete="tel-national"
                                className={`wizard-input phone-input ${phoneError ? 'input-error' : ''}`}
                                placeholder="(555) 123-4567"
                                value={formatPhoneDisplay(phone)}
                                onChange={handlePhoneChange}
                                maxLength={14}
                                autoFocus
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && isPhoneValid()) handleProceedFromPhone();
                                }}
                            />
                        </div>
                        {phoneError && <span className="input-error-text">{phoneError}</span>}
                    </div>

                    <button
                        className={`wizard-btn primary ${!isPhoneValid() ? 'disabled' : ''}`}
                        onClick={handleProceedFromPhone}
                        disabled={!isPhoneValid()}
                    >
                        {isSubmitting ? (
                            <>
                                <div className="btn-spinner"></div>
                                <span>Processing...</span>
                            </>
                        ) : phoneIsLast ? (
                            <>
                                <span>Checkout — ${(buyPrice * quantity).toFixed(2)}</span>
                                <i className="fas fa-arrow-right"></i>
                            </>
                        ) : (
                            <>
                                <span>Next</span>
                                <i className="fas fa-arrow-right"></i>
                            </>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    // Collected only for client-location services (isClientLoc). Always the
    // final step, so its button triggers checkout.
    const renderAddressStep = () => (
        <div className="step-content">
            <div className="step-body step-body-centered">
                <div className="step-icon-wrapper">
                    <i className="fas fa-map-marker-alt"></i>
                </div>
                <h1 className="step-title">Your address</h1>
                <p className="step-subtitle">This service will be conducted at a location of your choosing.</p>

                <div className="input-group">
                    <label className="input-label">Address</label>
                    <input
                        type="text"
                        name="address"
                        autoComplete="street-address"
                        className={`wizard-input ${addressError ? 'input-error' : ''}`}
                        placeholder="123 Main St, Los Angeles, CA 90001"
                        value={address}
                        onChange={(e) => {
                            setAddress(e.target.value);
                            if (addressError && e.target.value.trim().length >= 5) setAddressError('');
                        }}
                        autoFocus
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && isAddressValid()) handleProceedFromAddress();
                        }}
                    />
                    {addressError && <span className="input-error-text">{addressError}</span>}
                </div>

                <button
                    className={`wizard-btn primary ${!isAddressValid() ? 'disabled' : ''}`}
                    onClick={handleProceedFromAddress}
                    disabled={!isAddressValid()}
                >
                    {isSubmitting ? (
                        <>
                            <div className="btn-spinner"></div>
                            <span>Processing...</span>
                        </>
                    ) : (
                        <>
                            <span>Checkout — ${(buyPrice * quantity).toFixed(2)}</span>
                            <i className="fas fa-arrow-right"></i>
                        </>
                    )}
                </button>
            </div>
        </div>
    );

    const renderSchedulingStep = () => (
        <div className="step-content">
            <div className="step-body">
                <h1 className="step-title">Schedule your sessions</h1>
                <p className="step-subtitle">
                    Pick {totalSessions} time slot{totalSessions > 1 ? 's' : ''} that work for you.
                </p>

                {/* Progress counter */}
                <div className="session-counter">
                    <div className="counter-track">
                        <div className="counter-fill" style={{ width: `${(selectedSessions.length / totalSessions) * 100}%` }} />
                    </div>
                    <span className="counter-label">{selectedSessions.length} of {totalSessions} scheduled</span>
                </div>

                {renderCalendarUI({ capSessions: totalSessions })}

                {/* Scheduling is no longer the final step — it continues to the
                    email/phone inputs — so this advances rather than checks out. */}
                <button
                    className={`wizard-btn primary ${selectedSessions.length !== totalSessions ? 'disabled' : ''}`}
                    onClick={handleScheduleConfirm}
                    disabled={selectedSessions.length !== totalSessions}
                >
                    <span>Continue</span>
                    <i className="fas fa-arrow-right"></i>
                </button>
            </div>
        </div>
    );

    // Determine animation class
    const getAnimClass = () => {
        if (isAnimating) return slideDirection === 'next' ? 'slide-out-left' : 'slide-out-right';
        return 'slide-in';
    };

    return (
        <div className="sp-root">
            <Helmet>
                <title>{service?.name || 'Services'} | Verca</title>
                <meta property="og:title" content={`${service?.name || 'Services'} | Verca`} />
                <meta property="og:description" content={`${service?.bundleqty} sessions - ${service?.category}`} />
                <meta property="og:image" content={service?.media?.[0] || 'https://yoursite.com/default-thumbnail.jpg'} />
                <meta property="og:url" content={`https://verca.io/services/${id}`} />
                <meta property="og:type" content="product" />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={`${service?.name || 'Services'} | Verca`} />
                <meta name="twitter:description" content={`${service?.bundleqty} sessions - ${service?.category}`} />
                <meta name="twitter:image" content={service?.media?.[0] || 'https://yoursite.com/default-thumbnail.jpg'} />
            </Helmet>

            <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css" />
            <link href="https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;1,9..40,400&display=swap" rel="stylesheet" />

            {/* Nav */}
            <nav className="sp-nav">
                <div className="sp-nav-inner">
                    <div className="sp-logo" onClick={() => navigate('/')}>
                        <span className="sp-logo-text">Verca</span>
                    </div>
                </div>
            </nav>

            {/* Header bar with back button — always rendered once service loads.
                Back button is just hidden visually on the details step, so its
                position never shifts and it appears stationary across transitions. */}
            {!loading && service && (
                <div className="sp-header">
                    <div className="sp-header-inner">
                        <button
                            className="sp-back"
                            onClick={handleBack}
                            style={{
                                visibility: step === 'details' ? 'hidden' : 'visible',
                                pointerEvents: step === 'details' ? 'none' : 'auto'
                            }}
                            aria-hidden={step === 'details'}
                            tabIndex={step === 'details' ? -1 : 0}
                        >
                            <i className="fas fa-arrow-left"></i>
                        </button>
                    </div>
                </div>
            )}

            {/* Main content */}
            <main className="sp-main">
                <div className="sp-container">
                    {loading ? (
                        <div className="sp-loading">
                            <div className="sp-spinner"></div>
                            <p>Loading service details...</p>
                        </div>
                    ) : service ? (
                        // No `key={step}` here — keeping the same DOM node lets the CSS
                        // transition handle the slide animation, and lets us keep the
                        // details step (with its hero image) mounted at all times via
                        // display toggling, which prevents the image from re-mounting
                        // and flickering when the user navigates back.
                        <div ref={contentRef} className={`sp-animated ${getAnimClass()}`}>
                            {/* Details is always mounted; we just hide it when off-screen
                                so the hero <img> never gets unmounted/re-fetched. */}
                            <div style={{ display: step === 'details' ? 'block' : 'none' }}>
                                {renderDetailsStep()}
                            </div>
                            {step === 'scheduling' && renderSchedulingStep()}
                            {step === 'name' && renderNameStep()}
                            {step === 'email' && renderEmailStep()}
                            {step === 'phone' && renderPhoneStep()}
                            {step === 'address' && renderAddressStep()}
                        </div>
                    ) : (
                        <div className="sp-error">
                            <div className="sp-error-icon"><i className="fas fa-search"></i></div>
                            <h2>Service Not Found</h2>
                            <p>Unable to locate service</p>
                            <button className="wizard-btn secondary" onClick={() => navigate('/')}>Back to Home</button>
                        </div>
                    )}
                </div>
            </main>

            <style>{`
                /* ── Reset & Base ── */
                *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

                /* Monochrome checkout palette (Square-style):
                   #FFFFFF page · #F6F6F6 surface · #EBEBEB hover · #E0E0E0 hairline
                   #000000 ink · #767676 secondary · #A3A3A3 tertiary */
                .sp-root {
                    min-height: 100vh;
                    background: #FFFFFF;
                    font-family: 'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif;
                    color: #000000;
                    -webkit-font-smoothing: antialiased;
                }

                /* ── Nav ── */
                .sp-nav {
                    position: fixed; top: 0; left: 0; right: 0; z-index: 100;
                    padding: 1.25rem 1.5rem;
                    background: rgba(255, 255, 255, 0.85);
                    backdrop-filter: blur(16px);
                    border-bottom: 1px solid #EDEDED;
                }
                .sp-nav-inner { max-width: 640px; margin: 0 auto; display: flex; align-items: center; }
                .sp-logo { display: flex; align-items: center; gap: 10px; cursor: pointer; transition: opacity 0.2s; }
                .sp-logo:hover { opacity: 0.65; }
                .sp-logo-icon {
                    width: 40px; height: 40px;
                    background: #000000;
                    border-radius: 12px; display: flex; align-items: center; justify-content: center;
                }
                .sp-logo-icon i { font-size: 1rem; color: #FFFFFF; }
                .sp-logo-text { font-size: 1.35rem; font-weight: 700; letter-spacing: -0.5px; color: #000000; }

                /* ── Header / Progress ── */
                .sp-header {
                    position: fixed; top: 75px; left: 0; right: 0; z-index: 99;
                    background: transparent;
                }
                .sp-header-inner {
                    max-width: 640px; margin: 0 auto;
                    display: flex; align-items: center; gap: 16px;
                    padding: 12px 1.5rem;
                }
                .sp-back {
                    width: 40px; height: 40px; border-radius: 12px;
                    background: #F6F6F6; border: 1px solid #E6E6E6;
                    color: #000000; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    transition: background 0.2s, border-color 0.2s;
                }
                .sp-back:hover { background: #EBEBEB; border-color: #D6D6D6; }
                .sp-back:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }

                /* ── Main ── */
                .sp-main { padding-top: 130px; min-height: 100vh; background: #FFFFFF; }
                .sp-container { max-width: 640px; margin: 0 auto; padding: 0 1.5rem 4rem; }

                /* ── Transitions (fade in / fade out) ── */
                .sp-animated {
                    transition: opacity 320ms cubic-bezier(0.32, 0, 0.24, 1),
                                transform 320ms cubic-bezier(0.32, 0, 0.24, 1);
                    will-change: opacity, transform;
                }
                .slide-in { opacity: 1; transform: translateY(0) scale(1); }
                /* Outgoing steps fade out slightly faster than the incoming fade in,
                   which reads as a clean crossfade rather than a hard swap. */
                .slide-out-left,
                .slide-out-right {
                    opacity: 0;
                    transition-duration: 200ms;
                    transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
                }
                .slide-out-left { transform: translateY(6px) scale(0.995); }
                .slide-out-right { transform: translateY(-6px) scale(0.995); }

                @keyframes fadeSlideIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }

                @media (prefers-reduced-motion: reduce) {
                    .sp-animated { transition-duration: 1ms; }
                    .step-content { animation: none; }
                    .description-body { transition: none; }
                    .info-card:hover, .wizard-btn.primary:hover:not(.disabled) { transform: none; }
                }

                /* ── Loading ── */
                .sp-loading {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; min-height: 50vh; gap: 1.25rem;
                }
                .sp-loading p { color: #767676; font-size: 0.95rem; }
                .sp-spinner {
                    width: 40px; height: 40px;
                    border: 2px solid #E6E6E6;
                    border-top-color: #000000; border-radius: 50%;
                    animation: spin 0.8s linear infinite;
                }
                @keyframes spin { to { transform: rotate(360deg); } }

                /* ── Error ── */
                .sp-error {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; min-height: 50vh; text-align: center;
                    animation: fadeSlideIn 0.4s ease;
                }
                .sp-error-icon {
                    width: 72px; height: 72px; border-radius: 20px;
                    background: #F6F6F6;
                    display: flex; align-items: center; justify-content: center; margin-bottom: 1.5rem;
                }
                .sp-error-icon i { font-size: 1.75rem; color: #A3A3A3; }
                .sp-error h2 { font-size: 1.75rem; font-weight: 700; margin-bottom: 0.5rem; letter-spacing: -0.5px; color: #000000; }
                .sp-error p { color: #767676; margin-bottom: 2rem; }

                /* ── Step Content ── */
                .step-content { animation: fadeSlideIn 0.35s cubic-bezier(0.32, 0, 0.24, 1); }
                .step-body { display: flex; flex-direction: column; gap: 0; }
                .step-body-centered { padding-top: 2rem; }

                /* ── Hero Image ── */
                .hero-image-wrapper {
                    border-radius: 20px; overflow: hidden; margin-bottom: 2rem;
                    position: relative;
                    border: 1px solid #EDEDED;
                }
                .hero-image { width: 100%; height: 280px; object-fit: cover; display: block; }
                .hero-image-overlay {
                    position: absolute; bottom: 0; left: 0; right: 0; height: 50%;
                    background: linear-gradient(transparent, rgba(0, 0, 0, 0.06));
                    pointer-events: none;
                }

                /* ── Typography ── */
                .server-label {
                    color: #767676; font-size: 13px; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 2px; margin-bottom: 8px; display: block;
                }
                .step-title {
                    font-size: 2rem; font-weight: 700; color: #000000;
                    letter-spacing: -0.75px; line-height: 1.15; margin-bottom: 12px;
                }
                .step-subtitle {
                    font-size: 1rem; color: #767676; line-height: 1.55; margin-bottom: 28px;
                }

                .step-icon-wrapper {
                    width: 56px; height: 56px; border-radius: 16px;
                    background: #F2F2F2;
                    display: flex; align-items: center; justify-content: center;
                    margin-bottom: 1.5rem;
                }
                .step-icon-wrapper i { font-size: 1.35rem; color: #000000; }
                .step-icon-wrapper.purple { background: #F2F2F2; }
                .step-icon-wrapper.purple i { color: #000000; }

                /* ── Meta pills ── */
                .meta-pills { display: flex; align-items: center; gap: 10px; margin-bottom: 24px; flex-wrap: wrap; }
                .meta-pill {
                    color: #444444; font-size: 14px; font-weight: 600;
                    background: #F6F6F6; padding: 6px 14px; border-radius: 10px;
                    letter-spacing: 0.5px;
                }
                .meta-dot { color: #C4C4C4; }

                /* ── Info Cards ── */
                .info-cards { display: flex; flex-direction: column; gap: 10px; margin-top: 28px; margin-bottom: 8px; }
                .info-card {
                    display: flex; align-items: center; gap: 14px;
                    background: #FFFFFF;
                    border: 1px solid #E6E6E6;
                    border-radius: 16px;
                    padding: 16px 18px; cursor: pointer; transition: all 0.2s;
                    color: #000000;
                }
                .info-card:hover {
                    background: #FAFAFA;
                    border-color: #D6D6D6;
                    transform: translateY(-1px);
                }
                .info-card-icon {
                    width: 44px; height: 44px; border-radius: 13px;
                    background: #F2F2F2;
                    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
                }
                .info-card-icon i { font-size: 1.1rem; color: #000000; }
                .info-card-icon.phone-icon { background: #F2F2F2; }
                .info-card-icon.phone-icon i { color: #000000; }
                .info-card-text { flex: 1; display: flex; flex-direction: column; gap: 2px; }
                .info-card-label { font-size: 12px; color: #767676; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
                .info-card-value { font-size: 15px; font-weight: 500; color: #000000; }
                .info-card-arrow { color: #A3A3A3; font-size: 12px; }

                /* ── Section Label ── */
                .section-label {
                    font-size: 14px; font-weight: 600; color: #767676;
                    margin-bottom: 12px; display: block;
                }

                /* ── Description ── */
                .description-section { margin-bottom: 28px; }
                .description-body {
                    position: relative;
                    overflow: hidden;
                    max-height: 2000px;
                    transition: max-height 0.3s cubic-bezier(0.32, 0, 0.24, 1);
                }
                /* ~4 lines at the 1.65 line-height set below */
                .description-body.collapsed { max-height: 6.6em; }
                .description-text {
                    font-size: 15px;
                    line-height: 1.65;
                    color: #444444;
                    white-space: pre-wrap;
                    overflow-wrap: anywhere;
                }
                .description-fade {
                    position: absolute; left: 0; right: 0; bottom: 0; height: 2.6em;
                    background: linear-gradient(rgba(255,255,255,0), #FFFFFF 85%);
                    pointer-events: none;
                }
                .description-toggle {
                    display: inline-flex; align-items: center; gap: 6px;
                    margin-top: 8px; padding: 6px 0;
                    background: transparent; border: none;
                    color: #000000; font-size: 14px; font-weight: 600;
                    font-family: 'DM Sans', sans-serif; cursor: pointer;
                    transition: opacity 0.2s;
                }
                .description-toggle:hover { opacity: 0.6; }
                .description-toggle:focus-visible { outline: 2px solid #000000; outline-offset: 3px; border-radius: 6px; }
                .description-toggle i { font-size: 11px; }

                /* ── Quantity ── */
                .quantity-section { margin-bottom: 24px; }
                .quantity-row {
                    display: flex; align-items: center; gap: 0;
                    background: #F6F6F6;
                    border: 1px solid #E6E6E6;
                    border-radius: 16px; padding: 6px; width: fit-content;
                }
                .qty-btn {
                    width: 42px; height: 42px; border-radius: 12px;
                    border: none; background: #FFFFFF;
                    color: #000000; cursor: pointer;
                    display: flex; align-items: center; justify-content: center;
                    transition: all 0.2s; font-size: 14px;
                    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
                }
                .qty-btn:hover:not(:disabled) { background: #000000; color: #FFFFFF; }
                .qty-btn:disabled { opacity: 0.3; cursor: not-allowed; box-shadow: none; }
                .qty-display { padding: 0 24px; text-align: center; }
                .qty-number { display: block; font-size: 1.35rem; font-weight: 700; color: #000000; }
                .qty-label { font-size: 11px; color: #767676; text-transform: uppercase; letter-spacing: 0.5px; }

                /* ── Inline schedule wrapper (on details step) ── */
                .inline-schedule { margin-bottom: 28px; }

                /* ── Payment ── */
                .payment-section { margin-bottom: 28px; }

                /* ── Inputs ── */
                .input-group { margin-bottom: 24px; }
                .input-label {
                    font-size: 14px; font-weight: 500; color: #767676;
                    margin-bottom: 8px; display: block;
                }
                .wizard-input {
                    width: 100%; background: #FFFFFF;
                    border: 1px solid #D6D6D6; border-radius: 14px;
                    padding: 16px 18px; font-size: 17px; color: #000000;
                    font-family: 'DM Sans', sans-serif; outline: none; transition: all 0.2s;
                }
                .wizard-input::placeholder { color: #A3A3A3; }
                .wizard-input:focus { border-color: #000000; box-shadow: 0 0 0 1px #000000; }
                .wizard-input.input-error { border-color: #CC0023; box-shadow: none; }
                .input-error-text { font-size: 13px; color: #CC0023; margin-top: 8px; display: block; }

                .phone-field {
                    display: flex; align-items: center;
                    background: #FFFFFF;
                    border: 1px solid #D6D6D6;
                    border-radius: 14px;
                    overflow: hidden; transition: all 0.2s;
                }
                .phone-field:focus-within { border-color: #000000; box-shadow: 0 0 0 1px #000000; }
                .phone-prefix {
                    padding: 0 0 0 18px; font-size: 17px; color: #767676;
                    font-weight: 500; user-select: none;
                }
                .phone-input {
                    border: none !important; border-radius: 0 !important;
                    background: transparent !important;
                    padding-left: 8px !important;
                    box-shadow: none !important;
                }
                .phone-input:focus { box-shadow: none !important; }

                .phone-input:-webkit-autofill,
                .phone-input:-webkit-autofill:hover,
                .phone-input:-webkit-autofill:focus {
                    -webkit-box-shadow: 0 0 0 1000px #FFFFFF inset;
                    -webkit-text-fill-color: #000000;
                    transition: background-color 5000s ease-in-out 0s;
                }

                /* ── Buttons ── */
                .wizard-btn {
                    display: flex; align-items: center; justify-content: center; gap: 10px;
                    width: 100%; padding: 18px 24px; border-radius: 18px;
                    font-size: 16px; font-weight: 600; font-family: 'DM Sans', sans-serif;
                    border: none; cursor: pointer; transition: all 0.25s; letter-spacing: 0.3px;
                }
                .wizard-btn:focus-visible { outline: 2px solid #000000; outline-offset: 2px; }
                .wizard-btn.primary {
                    background: #000000; color: #FFFFFF;
                }
                .wizard-btn.primary:hover:not(.disabled) {
                    background: #1F1F1F;
                    transform: translateY(-2px);
                }
                .wizard-btn.primary.disabled {
                    background: #E6E6E6; color: #9A9A9A;
                    cursor: not-allowed; transform: none;
                }
                .wizard-btn.secondary {
                    background: #FFFFFF; color: #000000;
                    border: 1px solid #D6D6D6;
                }
                .wizard-btn.secondary:hover { background: #F6F6F6; border-color: #B8B8B8; }

                .btn-spinner {
                    width: 20px; height: 20px;
                    border: 2px solid rgba(255,255,255,0.25); border-radius: 50%;
                    border-top-color: #FFFFFF; animation: spin 0.7s linear infinite;
                }

                /* ── Scheduling ── */
                .session-counter {
                    background: #F6F6F6;
                    border: 1px solid #EDEDED;
                    border-radius: 14px; padding: 16px 18px; margin-bottom: 24px;
                }
                .counter-track {
                    height: 6px; background: #E0E0E0;
                    border-radius: 3px; overflow: hidden; margin-bottom: 10px;
                }
                .counter-fill {
                    height: 100%; background: #000000; border-radius: 3px;
                    transition: width 0.35s ease;
                }
                .counter-label { font-size: 14px; color: #444444; font-weight: 500; }

                .schedule-grid { display: grid; grid-template-columns: 1fr; gap: 16px; margin-bottom: 24px; }
                @media (min-width: 640px) { .schedule-grid { grid-template-columns: 1fr 1fr; } }

                .cal-card, .slots-card {
                    background: #FFFFFF;
                    border: 1px solid #E6E6E6;
                    border-radius: 16px; padding: 20px;
                }
                .cal-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
                .cal-month { font-size: 1rem; font-weight: 600; color: #000000; }
                .cal-nav {
                    width: 34px; height: 34px; border-radius: 10px;
                    background: #F6F6F6; border: 1px solid #E6E6E6;
                    color: #000000; cursor: pointer;
                    display: flex; align-items: center; justify-content: center; transition: all 0.2s;
                }
                .cal-nav:hover { background: #EBEBEB; border-color: #D6D6D6; }

                .cal-weekdays { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; margin-bottom: 4px; }
                .cal-weekday {
                    text-align: center; font-size: 11px; font-weight: 600;
                    color: #A3A3A3; text-transform: uppercase; padding: 6px 0;
                }
                .cal-days { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
                .cal-day {
                    aspect-ratio: 1; display: flex; align-items: center; justify-content: center;
                    border-radius: 10px; background: #FFFFFF;
                    cursor: pointer; transition: all 0.15s; font-size: 14px; font-weight: 500;
                    color: #000000;
                }
                .cal-day:not(.empty):not(.off):hover {
                    background: #F0F0F0;
                }
                .cal-day.off { color: #C4C4C4; background: #FFFFFF; cursor: not-allowed; }
                .cal-day.selected { background: #000000; color: #FFFFFF; font-weight: 700; }
                .cal-day.booked { background: #E8E8E8; color: #000000; }
                .cal-day.selected.booked { background: #000000; color: #FFFFFF; }
                .cal-day.empty { background: transparent; border: none; cursor: default; }

                /* ── Hotel-style day-span calendar ── */
                .hotel-schedule { margin-bottom: 8px; }
                .hotel-cal { margin-bottom: 16px; }
                .cal-day.in-range {
                    background: #EDEDED;
                    color: #000000;
                }
                .cal-day.checkin, .cal-day.checkout {
                    background: #000000; color: #FFFFFF; font-weight: 700;
                }
                .cal-day.checkin:hover, .cal-day.checkout:hover {
                    background: #000000;
                }
                .hotel-summary {
                    background: #F6F6F6;
                    border: 1px solid #EDEDED;
                    border-radius: 16px; padding: 18px 20px;
                    display: flex; flex-direction: column; gap: 14px;
                }
                .hotel-dates { display: flex; align-items: center; gap: 12px; }
                .hotel-date-block { display: flex; flex-direction: column; gap: 4px; flex: 1; }
                .hotel-date-block-right { align-items: flex-end; text-align: right; }
                .hotel-date-label {
                    font-size: 12px; color: #767676; font-weight: 600;
                    text-transform: uppercase; letter-spacing: 0.5px;
                }
                .hotel-date-value { font-size: 15px; font-weight: 600; color: #000000; }
                .hotel-date-value.empty { color: #A3A3A3; font-weight: 500; }
                .hotel-arrow { color: #A3A3A3; font-size: 13px; flex-shrink: 0; }
                .hotel-total {
                    display: flex; align-items: center; justify-content: space-between;
                    padding-top: 14px; border-top: 1px solid #E0E0E0;
                }
                .hotel-total-left {
                    display: flex; align-items: center; gap: 8px;
                    color: #000000; font-size: 14px; font-weight: 600;
                }
                .hotel-total-left i { font-size: 12px; color: #767676; }
                .hotel-total-price { font-size: 16px; font-weight: 700; color: #000000; }
                .hotel-hint {
                    font-size: 13px; color: #767676;
                    padding-top: 14px; border-top: 1px solid #E0E0E0;
                }

                .slots-heading { font-size: 15px; font-weight: 600; color: #000000; margin-bottom: 14px; }
                .slots-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 8px; }
                .slot-btn {
                    background: #FFFFFF; border: 1px solid #D6D6D6;
                    color: #000000; padding: 10px 8px; border-radius: 10px;
                    font-size: 13px; font-weight: 500; cursor: pointer; transition: all 0.15s;
                    font-family: 'DM Sans', sans-serif;
                }
                .slot-btn:hover:not(:disabled) {
                    background: #F0F0F0; border-color: #000000;
                }
                .slot-btn.active { background: #000000; border-color: #000000; color: #FFFFFF; font-weight: 600; }
                .slot-btn:disabled { opacity: 0.35; cursor: not-allowed; }

                .slots-placeholder {
                    display: flex; flex-direction: column; align-items: center;
                    justify-content: center; padding: 3rem 1rem;
                    color: #A3A3A3; text-align: center;
                }
                .slots-placeholder i { font-size: 2rem; margin-bottom: 12px; }
                .slots-placeholder p { font-size: 14px; }
                .slots-empty { color: #767676; text-align: center; padding: 2rem 0; font-size: 14px; }

                /* ── Selected Sessions ── */
                .selected-summary {
                    background: #F6F6F6;
                    border: 1px solid #EDEDED;
                    border-radius: 16px; padding: 18px 20px; margin-bottom: 24px;
                }
                .summary-title { font-size: 15px; font-weight: 600; color: #000000; margin-bottom: 12px; }
                .summary-list { display: flex; flex-direction: column; gap: 8px; }
                .summary-item {
                    display: flex; justify-content: space-between; align-items: center;
                    background: #FFFFFF; border: 1px solid #E6E6E6;
                    padding: 12px 14px; border-radius: 10px;
                    font-size: 14px; color: #000000;
                }
                .summary-remove {
                    background: transparent; border: none;
                    color: #A3A3A3; cursor: pointer;
                    padding: 4px; transition: color 0.15s;
                }
                .summary-remove:hover { color: #CC0023; }

                /* ── Related ── */
                .related-section { margin-top: 48px; }
                .related-title { font-size: 1.5rem; font-weight: 700; letter-spacing: -0.5px; margin-bottom: 4px; color: #000000; }
                .related-subtitle { font-size: 14px; color: #767676; margin-bottom: 16px; }
                .related-scroll {
                    display: flex; overflow-x: auto; gap: 16px; padding: 8px 0;
                    -webkit-overflow-scrolling: touch;
                }
                .related-scroll::-webkit-scrollbar { display: none; }

                /* ── Responsive ── */
                @media (max-width: 640px) {
                    .step-title { font-size: 1.65rem; }
                    .hero-image { height: 220px; }
                    .sp-container { padding: 0 1.25rem 3rem; }
                    .schedule-grid { grid-template-columns: 1fr; }
                }
            `}</style>
        </div>
    );
};

export default ServicesPage;